/**
 * Mega Nav — interactive behavior for desktop and mobile navigation.
 *
 * Desktop: hover/click mega dropdown panels with keyboard support.
 * Mobile: full-screen overlay with slide-in sub-panels.
 *
 * @file assets/src/js/headers/mega-nav.js
 */
document.addEventListener('DOMContentLoaded', () => {

	/**
	 * -------------------------------------------------------------------------
	 * Mobile menu
	 * -------------------------------------------------------------------------
	 */

	const toggle = document.getElementById('main-nav-toggle');
	const menu = document.getElementById('main-nav-mobile');
	const closeBtn = document.getElementById('main-nav-close');
	const mainList = document.getElementById('mobile-main-list');
	let activeMobilePanel = null;

	/** Cache mobile sub-panels and their trigger buttons by index. */
	const subPanelMap = {};
	const subPanelBtnMap = {};

	document.querySelectorAll('[data-mobile-subpanel]').forEach((el) => {
		subPanelMap[el.dataset.mobileSubpanel] = el;
	});

	document.querySelectorAll('[data-mobile-open-panel]').forEach((btn) => {
		subPanelBtnMap[btn.dataset.mobileOpenPanel] = btn;
	});

	/**
	 * Return to the top-level mobile menu list, hiding any open sub-panel.
	 */
	function showMainList() {
		if (activeMobilePanel !== null) {
			const prev = subPanelMap[activeMobilePanel];
			if (prev) prev.classList.add('tw-hidden');
			const prevBtn = subPanelBtnMap[activeMobilePanel];
			if (prevBtn) prevBtn.setAttribute('aria-expanded', 'false');
			activeMobilePanel = null;
		}
		if (mainList) mainList.classList.remove('tw-hidden');
	}

	/**
	 * Slide a mobile sub-panel into view by index.
	 *
	 * @param {string|number} index - The data-mobile-subpanel index to open.
	 */
	function showSubPanel(index) {
		if (mainList) mainList.classList.add('tw-hidden');

		if (activeMobilePanel !== null && activeMobilePanel !== index) {
			const prev = subPanelMap[activeMobilePanel];
			if (prev) prev.classList.add('tw-hidden');
		}

		const panel = subPanelMap[index];
		if (!panel) return;

		activeMobilePanel = index;
		panel.classList.remove('tw-hidden');
		panel.scrollTop = 0;

		const btn = subPanelBtnMap[index];
		if (btn) btn.setAttribute('aria-expanded', 'true');
	}

	/**
	 * Open the full-screen mobile menu overlay and lock body scroll.
	 */
	function openMobileMenu() {
		if (!menu || !toggle) return;
		menu.classList.remove('tw-hidden');
		menu.classList.add('tw-flex');
		toggle.setAttribute('aria-expanded', 'true');
		document.body.style.overflow = 'hidden';
	}

	/**
	 * Close the mobile menu overlay, reset sub-panels, and restore body scroll.
	 */
	function closeMobileMenu() {
		if (!menu || !toggle) return;
		showMainList();
		menu.classList.add('tw-hidden');
		menu.classList.remove('tw-flex');
		toggle.setAttribute('aria-expanded', 'false');
		document.body.style.overflow = '';
	}

	if (toggle && menu) {
		toggle.addEventListener('click', openMobileMenu);
	}

	if (closeBtn) {
		closeBtn.addEventListener('click', closeMobileMenu);
	}

	/** Mobile sub-panel open buttons. */
	Object.keys(subPanelBtnMap).forEach((index) => {
		subPanelBtnMap[index].addEventListener('click', () => {
			showSubPanel(index);
		});
	});

	/** Mobile sub-panel back buttons. */
	document.querySelectorAll('[data-mobile-back]').forEach((btn) => {
		btn.addEventListener('click', showMainList);
	});

	/**
	 * -------------------------------------------------------------------------
	 * Desktop mega menu
	 * -------------------------------------------------------------------------
	 */

	const megaItems = document.querySelectorAll('[data-mega-menu]');
	const closeTimers = {};
	let activeId = null;

	/** Cache desktop panel elements and trigger buttons by ID. */
	const btnCache = {};
	const panelCache = {};

	megaItems.forEach((li) => {
		const id = li.getAttribute('data-mega-menu');
		btnCache[id] = document.querySelector(`[data-mega-btn="${id}"]`);
		panelCache[id] = document.querySelector(`[data-mega-panel="${id}"]`);
	});

	/**
	 * Get the cached trigger button for a mega panel by ID.
	 *
	 * @param {string} id - The data-mega-btn value.
	 * @returns {Element|null}
	 */
	function getBtn(id) {
		return btnCache[id] ?? null;
	}

	/**
	 * Get the cached mega dropdown panel by ID.
	 *
	 * @param {string} id - The data-mega-panel value.
	 * @returns {Element|null}
	 */
	function getPanel(id) {
		return panelCache[id] ?? null;
	}

	/**
	 * Open a mega dropdown panel, closing any previously open panel.
	 *
	 * @param {string}  id        - The panel ID to open.
	 * @param {boolean} moveFocus - Whether to move focus into the panel.
	 */
	function openPanel(id, moveFocus = false) {
		clearTimeout(closeTimers[id]);
		closeAllPanels();

		const panel = getPanel(id);
		const btn = getBtn(id);
		if (!panel || !btn) return;

		activeId = id;
		panel.classList.remove('tw-hidden');
		btn.setAttribute('aria-expanded', 'true');
		btn.classList.add('tw-text-brand-secondary', 'tw-border-brand-secondary');
		btn.classList.remove('tw-border-transparent');

		const chevron = btn.querySelector('svg');
		if (chevron) chevron.style.transform = 'rotate(180deg)';

		if (!moveFocus) return;

		const firstLink = panel.querySelector('a');
		if (firstLink) firstLink.focus();
	}

	/**
	 * Close a single mega dropdown panel and reset its trigger state.
	 *
	 * @param {string} id - The panel ID to close.
	 */
	function closePanel(id) {
		const panel = getPanel(id);
		const btn = getBtn(id);
		if (!panel || !btn) return;

		panel.classList.add('tw-hidden');
		btn.setAttribute('aria-expanded', 'false');
		btn.classList.remove('tw-text-brand-secondary', 'tw-border-brand-secondary');
		btn.classList.add('tw-border-transparent');

		const chevron = btn.querySelector('svg');
		if (chevron) chevron.style.transform = '';

		if (activeId === id) activeId = null;
	}

	/**
	 * Close all open mega dropdown panels.
	 */
	function closeAllPanels() {
		megaItems.forEach((li) => {
			const id = li.getAttribute('data-mega-menu');
			closePanel(id);
		});
		activeId = null;
	}

	/**
	 * Schedule a panel close with a 150ms delay to prevent flicker on hover.
	 *
	 * @param {string} id - The panel ID to schedule closing.
	 */
	function scheduleClose(id) {
		closeTimers[id] = setTimeout(() => {
			closePanel(id);
		}, 150);
	}

	megaItems.forEach((li) => {
		const id = li.getAttribute('data-mega-menu');
		const btn = getBtn(id);
		const panel = getPanel(id);
		if (!btn || !panel) return;

		li.addEventListener('mouseenter', () => {
			clearTimeout(closeTimers[id]);
			openPanel(id);
		});

		li.addEventListener('mouseleave', () => {
			scheduleClose(id);
		});

		panel.addEventListener('mouseenter', () => {
			clearTimeout(closeTimers[id]);
		});

		panel.addEventListener('mouseleave', () => {
			scheduleClose(id);
		});

		btn.addEventListener('click', (e) => {
			e.stopPropagation();
			const expanded = btn.getAttribute('aria-expanded') === 'true';

			if (expanded) {
				closePanel(id);
				return btn.focus();
			}

			openPanel(id, true);
		});
	});

	/** Close panels when clicking outside the nav. */
	document.addEventListener('click', (e) => {
		if (activeId === null) return;

		const inNav = e.target.closest('[data-mega-menu], [data-mega-panel]');
		if (inNav) return;

		closeAllPanels();
	});

	/** Close on Escape and return focus to the trigger button. */
	document.addEventListener('keydown', (e) => {
		if (e.key !== 'Escape' || activeId === null) return;

		const btn = getBtn(activeId);
		closeAllPanels();
		if (btn) btn.focus();
	});

	/** Close everything on resize or orientation change. */
	let resizeTimer;
	window.addEventListener('resize', () => {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(() => {
			closeAllPanels();
			closeMobileMenu();
		}, 100);
	});
});
